/**
 * Persistence boundary for draft document content.
 *
 * v1 stores HTML strings in Firestore. Future versions may use ProseMirror/TipTap JSON
 * as the canonical format — callers should use this module instead of reading/writing
 * tabs[].content directly.
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  DEFAULT_LINE_SPACING,
  DEFAULT_PARAGRAPH_SPACING_PT,
} from "./editor-defaults";
import { db } from "./firebase";

/** v1: HTML only. Future: union with ProseMirror JSON document node. */
export type DocumentContentFormat = "html";

export interface DocumentTab {
  id: string;
  title: string;
  content: string;
  paragraphSpacing?: number;
  lineSpacing?: string;
}

export interface LoadedDocument {
  id: string;
  title: string;
  tabs: DocumentTab[];
  activeTabId: string;
  caseId?: string;
  type?: string;
  contentFormat: DocumentContentFormat;
}

export interface SaveDocumentPayload {
  title: string;
  tabs: DocumentTab[];
  activeTabId: string;
  caseId?: string;
  [key: string]: unknown;
}

export interface CreateDraftPayload {
  title: string;
  tabs: DocumentTab[];
  activeTabId: string;
  caseId?: string;
  lawyerId: string;
  workspaceId: string;
  documentType?: string;
  source?: string;
  [key: string]: unknown;
}

function withTabSpacingDefaults(tab: DocumentTab): DocumentTab {
  return {
    ...tab,
    paragraphSpacing: tab.paragraphSpacing ?? DEFAULT_PARAGRAPH_SPACING_PT,
    lineSpacing: tab.lineSpacing ?? DEFAULT_LINE_SPACING,
  };
}

/** Adapt stored content for TipTap `setContent`. */
export function serializeForEditor(rawContent: string): string {
  return rawContent;
}

/** Adapt editor output for Firestore persistence. */
export function serializeForStorage(editorHtml: string): string {
  return editorHtml;
}

export function legacyContentFromTabs(tabs: DocumentTab[]): string {
  return tabs[0]?.content ?? "";
}

export async function loadDocument(
  workspaceId: string,
  documentId: string,
): Promise<LoadedDocument | null> {
  const snap = await getDoc(
    doc(db, "workspaces", workspaceId, "documents", documentId),
  );
  if (!snap.exists()) return null;

  const data = snap.data();
  let tabs: DocumentTab[];
  let activeTabId: string;

  if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
    tabs = (data.tabs as DocumentTab[]).map(withTabSpacingDefaults);
    activeTabId = (data.activeTabId as string) || tabs[0].id;
  } else {
    const defaultTab: DocumentTab = withTabSpacingDefaults({
      id: "tab-1",
      title: (data.title as string) || "Document 1",
      content: serializeForEditor((data.content as string) || ""),
    });
    tabs = [defaultTab];
    activeTabId = "tab-1";
  }

  return {
    id: snap.id,
    title: (data.title as string) || "",
    tabs,
    activeTabId,
    caseId: data.caseId as string | undefined,
    type: data.type as string | undefined,
    contentFormat: "html",
  };
}

export async function saveDocument(
  workspaceId: string,
  documentId: string,
  payload: SaveDocumentPayload,
): Promise<void> {
  const tabs = payload.tabs.map((tab) => ({
    ...tab,
    content: serializeForStorage(tab.content),
  }));

  await updateDoc(doc(db, "workspaces", workspaceId, "documents", documentId), {
    ...payload,
    content: legacyContentFromTabs(tabs),
    tabs,
    updatedAt: serverTimestamp(),
  });
}

export async function createDraftDocument(
  payload: CreateDraftPayload,
): Promise<string> {
  const tabs = payload.tabs.map((tab) => ({
    ...tab,
    content: serializeForStorage(tab.content),
  }));

  const docRef = await addDoc(
    collection(db, "workspaces", payload.workspaceId, "documents"),
    {
      ...payload,
      type: "draft",
      content: legacyContentFromTabs(tabs),
      tabs,
      status: "draft",
      workflowStatus: "in_progress",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
  );

  return docRef.id;
}
